import { useEffect, useState } from 'react'
import type { Project, ProjectGitHub } from '../api/types'
import { errorMessage } from '../api/client'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { useMounted } from '../components/layout'

/** Today's App controls, on the server's operator-configured App. */
export function GitHubSettings({ project }: { project: Project }) {
  const store = useStore()
  const { me, offline } = useBoardState()
  if (store.byToken || !me?.member) return null
  return <Connection key={me.member.id} project={project} offline={offline} member={me.member.id} />
}

function Connection({ project, offline, member }: { project: Project; offline: boolean; member: string }) {
  const { api } = useSessionsEnv()
  const mounted = useMounted()
  const [status, setStatus] = useState<ProjectGitHub | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState(false)
  useEffect(() => {
    let active = true
    void api.github(project.id, member).then(answer => { if (active) setStatus(answer) }, error => { if (active) setProblem(errorMessage(error)) })
    return () => { active = false }
  }, [api, project.id, member])
  async function refresh() {
    if (busy || offline) return
    setBusy(true); setProblem(null)
    try {
      const answer = await api.refreshGitHub(project.id, member)
      if (mounted.current) { setStatus(answer); setRemoving(false) }
    } catch (error) { if (mounted.current) setProblem(errorMessage(error)) }
    finally { if (mounted.current) setBusy(false) }
  }
  const app = status?.snapshot.app
  const installed = !!app?.installation_id && app.installation_id === status?.installation_id
  const manage = 'https://github.com/settings/installations'
  return <section className="settings-section" aria-label="GitHub App">
    <h3>GitHub App</h3>
    {!status && !problem && <p role="status">Loading</p>}
    {status && <>
      {!status.configured ? <p>This server has no GitHub App for this workspace. Ask its operator to connect one.</p> : !status.repository ? <p>Connect a repository first.</p> : <>
        <p>{status.repository}</p>
        {app ? <>
          <p>{app.name} ({app.slug}[bot])</p>
          <p>{installed ? 'Connected on this server.' : 'Install the App on this repository only.'}</p>
          {app.installation_id && !installed && <p>The installation differs from this server’s. Ask its operator to update it.</p>}
          {installed && app.suspended && <p>The App is suspended on GitHub.</p>}
          {installed && !app.contents_write && <p>The App may only read this repository. Accept its permissions on GitHub.</p>}
          {!installed && <a href={`https://github.com/apps/${encodeURIComponent(app.slug)}/installations/new`} target="_blank" rel="noreferrer">Install on GitHub</a>}
          {installed && <button type="button" className="button" disabled={busy || offline} onClick={() => setRemoving(true)}>Disconnect</button>}
          <p><a href={manage} target="_blank" rel="noreferrer">Manage Apps on GitHub</a></p>
        </> : <p>No GitHub status to show yet.</p>}
        {status.snapshot.checked_at && <p>Last checked {new Date(status.snapshot.checked_at).toLocaleString()}.</p>}
        {status.snapshot.problem && <p role="alert" className="text-danger">{status.snapshot.problem}</p>}
        <button type="button" className="button" disabled={busy || offline} onClick={() => void refresh()}>{app && !installed ? 'Check installation' : 'Check again'}</button>
        {busy && <p role="status">Checking GitHub…</p>}
        {removing && <div className="settings-accept-band">
          <p>Remove the App on GitHub. This affects every repository in its installation.</p>
          <p>Then check the connection again.</p>
          <button type="button" className="button" onClick={() => setRemoving(false)}>Keep connected</button>
          <a href={manage} target="_blank" rel="noreferrer">Remove it on GitHub</a>
        </div>}
      </>}
    </>}
    {problem && <p role="alert" className="text-danger">{problem}</p>}
    {!status && problem && <button type="button" className="button" disabled={busy || offline} onClick={() => void refresh()}>Check again</button>}
  </section>
}
